import { Price, recordPriceDelimiter } from "../../price";
import { ContractIdentifier } from "./contracts";

/** Bid or ask as it arrives: `price` is a scaled integer in yen/MWh. */
type WireBidAsk = {
  is_own: boolean;
  price: number;
  kilo_watts: number;
};

/**
 * Top-of-book quote as it arrives, before {@link parseTopQuote}.
 *
 * A side with no resting order comes through as an explicit `null` rather than
 * an omitted key — these payloads are serialised by a Rust service, so `Option`
 * fields land as `null`. Hence `| null` rather than just optional.
 */
export type WireTopQuote = {
  price_delimiter?: number | null;
  contract_identifier: ContractIdentifier;
  top_bid?: WireBidAsk | null;
  top_ask?: WireBidAsk | null;
  publish_time: string; // ISO 8601 date string
};

// Define the type for Bid or Ask
export type BidAsk = {
  is_own: boolean;
  price: Price;
  kilo_watts: number;
};

// Define the main PriceObject type
export type TopQuote = {
  price_delimiter?: number;
  contract_identifier: ContractIdentifier;
  top_bid?: BidAsk;
  top_ask?: BidAsk;
  publish_time: string; // ISO 8601 date string
};

/**
 * Wire quote to stored quote.
 *
 * Scaling used to happen in place at ingest, which left a `TopQuote` meaning one
 * thing off the wire and another in the store with nothing in the type to say
 * which you held. Converting to `Price` here makes the two shapes distinct.
 *
 * `price_delimiter` is kept on the result: it is the venue's scale, and the REST
 * depth snapshots carry none of their own and borrow it.
 */
export const parseTopQuote = (wire: WireTopQuote): TopQuote => {
  const delimiter = wire.price_delimiter ?? undefined;
  recordPriceDelimiter("PRICES", delimiter);

  return {
    ...wire,
    price_delimiter: delimiter,
    // Normalised to undefined, so nothing downstream has to know the feed
    // spells "no bid" as null.
    top_bid: parseBidAsk(wire.top_bid, delimiter),
    top_ask: parseBidAsk(wire.top_ask, delimiter),
  };
};

const parseBidAsk = (
  wire: WireBidAsk | null | undefined,
  delimiter: number | undefined
): BidAsk | undefined =>
  wire === null || wire === undefined
    ? undefined
    : { ...wire, price: Price.fromScaledMwh(wire.price, delimiter) };
